import {
  faCirclePlus,
  faCloudUploadAlt,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Modal,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import ReusableEditor from "common/ReusableCKEditor";
import Toggle from "common/Toggle";
import mammoth from "mammoth";
import React, { useEffect, useState } from "react";
import { useDropzone, Accept } from "react-dropzone";
import toast from "react-hot-toast";
import { SubpagesEdit, SubpagesUpdate } from "services/apiService";
import { CoupnEditProps } from "types";

const SubPagesEdit: React.FC<CoupnEditProps> = ({
  open,
  id,
  handleClose,
  onActionComplete,
}) => {
  const style = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 900,
    bgcolor: "background.paper",
    border: "2px solid white",
    boxShadow: 24,
    p: 4,
  };

  const [subpagesForm, setSubpagesForm] = useState({
    page_name: "",
    page_content: "",
    is_active: 0,
  });

  const [fileName, setFileName] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [errors, setErrors] = useState({
    page_name: "",
    page_content: "",
  });

  const handleTextFieldChange = (e) => {
    const { id, value } = e.target;
    setSubpagesForm((prev) => ({
      ...prev,
      [id]: value,
    }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      [id]: "",
    }));
  };

  const handleToggleChange = (checked: number, field: string) => {
    setSubpagesForm((prevData) => ({
      ...prevData,
      [field]: checked ? 1 : 0,
    }));
  };

  const validateForm = () => {
    const newErrors: any = {};
    if (!subpagesForm.page_name) newErrors.page_name = "Title is required.";
    if (!subpagesForm.page_content)
      newErrors.page_content = "Page content is required.";
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const fetchSubPages = async (id: number) => {
    try {
      const response = await SubpagesEdit(id);
      if (response.status_code === 200) {
        setSubpagesForm({
          page_name: response.data.page_name,
          page_content: response.data.page_content,
          is_active: response.data.is_active,
        });
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleChangeCkeditor = (event: any, editor: any) => {
    const data = editor.getData();
    setSubpagesForm((prev) => ({
      ...prev,
      page_content: data,
    }));
  };
  const acceptFileTypes: Accept = {
    "application/msword": [".doc"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
      ".docx",
    ],
  };

  const onDrop = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      setIsUploading(true);
      const reader = new FileReader();
      reader.onload = (event) => {
        const arrayBuffer = event.target?.result as ArrayBuffer;
        mammoth
          .convertToHtml({ arrayBuffer })
          .then((result) => {
            setFileName(file.name);
            setSubpagesForm((prev) => ({
              ...prev,
              page_content: result.value,
            }));
            setIsUploading(false);
          })
          .catch((err) => {
            toast.error("Failed to extract content from the file.");
            setIsUploading(false);
          });
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: acceptFileTypes,
    multiple: false,
  });

  useEffect(() => {
    if (id) {
      fetchSubPages(id);
    }
  }, [open, id]);

  const handleSubmit = async () => {
    if (validateForm()) {
      try {
        const createPayload = {
          ...subpagesForm,
          id: id,
        };

        const response = await SubpagesUpdate(createPayload);
        if (response.status_code === 200) {
          toast.success("Successfully Updated");
          handleClose();
          if (onActionComplete) onActionComplete();
        } else {
          toast.error("Update Failed");
        }
      } catch (error) {
        console.log("error", error);
      }
    }
  };

  return (
    <Modal open={open} onClose={undefined}>
      <Box sx={style}>
        <Box sx={{ padding: 0 }}>
          <Box display="flex" flexDirection="column" gap={3}>
            <Box className="create_category_fields" mb={2}>
              <Typography variant="subtitle1" className="text-capitalize label">
                Title
              </Typography>
              <TextField
                variant="filled"
                id="page_name"
                fullWidth
                value={subpagesForm.page_name}
                onChange={handleTextFieldChange}
                error={!!errors.page_name}
                helperText={errors.page_name}
              />
            </Box>

            <Box
              className="create_category_fields"
              mb={2}
              sx={{
                height: 200,
                overflowY: "auto",

                borderRadius: "4px",
                p: 1,
              }}
            >
              <Typography variant="subtitle1" className="text-capitalize label">
                Page Content
              </Typography>
              <ReusableEditor
                value={subpagesForm.page_content || ""}
                onChange={(value) =>
                  setSubpagesForm((prev) => ({
                    ...prev,
                    page_content: value,
                  }))
                }
              />
            </Box>

            <Box className="create_category_fields" mb={2}>
              <Typography variant="subtitle1" className="text-capitalize label">
                Upload Document
              </Typography>
              <Paper
                {...getRootProps()}
                sx={{
                  padding: 3,
                  border: "2px dashed #1976d2",
                  textAlign: "center",
                  cursor: "pointer",
                  backgroundColor: "#f9f9f9",
                  "&:hover": { backgroundColor: "#e3f2fd" },
                }}
              >
                <input {...getInputProps()} />
                <FontAwesomeIcon
                  icon={faCloudUploadAlt}
                  style={{ fontSize: "40px", color: "#1976d2" }}
                />
                {isUploading ? (
                  <CircularProgress
                    size={24}
                    sx={{ color: "#1976d2", marginTop: 1 }}
                  />
                ) : (
                  <>
                    <Typography variant="body2" sx={{ color: "#1976d2" }}>
                      Drag & Drop your Word document here
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#1976d2" }}>
                      or click to select a file
                    </Typography>
                  </>
                )}
                {fileName && (
                  <Typography
                    variant="body2"
                    sx={{ color: "#1976d2", marginTop: 1 }}
                  >
                    {fileName}
                  </Typography>
                )}
              </Paper>
            </Box>
            <Box className="create_category_fields" mb={2}>
              <Typography variant="subtitle1" className="text-capitalize label">
                Status
              </Typography>
              <Toggle
                checked={subpagesForm.is_active === 1}
                onChange={(checked) => handleToggleChange(checked, "is_active")}
              />
            </Box>

            <Box pb={2}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={() => handleSubmit()}
                    type="submit"
                    disabled={
                      !(subpagesForm.page_name && subpagesForm.page_content)
                    }
                  >
                    <FontAwesomeIcon
                      style={{ marginRight: "10px" }}
                      icon={faCirclePlus}
                    />
                    Save changes
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => handleClose()}
                    fullWidth
                  >
                    Close
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default SubPagesEdit;
